const isFileObject = (value: any): value is File => {
    if (value instanceof File) {
        return true;
    }
    
    // Check for File-like object properties
    if (value && typeof value === 'object') {
        const hasFileProperties = 
            'name' in value && 
            'size' in value && 
            'type' in value &&
            (value.constructor?.name === 'File' || value instanceof Blob);
        return hasFileProperties;
    }
    
    return false;
};

export const processImageValue = async (
    imageValue: File | string | null | undefined,
    uploadFunction: (file: File) => Promise<string>,
    existingImageKey?: string | null
): Promise<string> => {
    if (imageValue === '[object File]' || (typeof imageValue === 'string' && imageValue.trim() === '[object File]')) {
        console.warn('Detected stringified File object "[object File]", using existing key or empty');
        return existingImageKey && typeof existingImageKey === 'string' ? existingImageKey : '';
    }
    
    // Check if it's a File object (must check BEFORE string check)
    if (isFileObject(imageValue)) {
        try {
            console.log('Processing File object:', imageValue.name, imageValue.size);
            const uploadedKey = await uploadFunction(imageValue as File);
            if (!uploadedKey) {
                console.warn('Image upload returned empty key');
                // Fallback to existing key if upload failed but returned empty
                return existingImageKey && typeof existingImageKey === 'string' ? existingImageKey : '';
            }
            console.log('Image uploaded successfully, key:', uploadedKey);
            return uploadedKey;
        } catch (error) {
            console.error('Image upload failed:', error);
            // On error, try to use existing key as fallback
            if (existingImageKey && typeof existingImageKey === 'string') {
                console.warn('Using existing image key due to upload error');
                return existingImageKey;
            }
            throw error;
        }
    }
    
    // If it's already a string (existing key), use it (but not if it's "[object File]")
    if (imageValue && typeof imageValue === 'string') {
        // Double check it's not the stringified File
        if (imageValue === '[object File]' || imageValue.trim() === '[object File]') {
            console.warn('String value is "[object File]", using existing key');
            return existingImageKey && typeof existingImageKey === 'string' ? existingImageKey : '';
        }
        // Valid string key
        return imageValue;
    }
    
    // If no new file and no existing value, use the existing key from API data
    if (existingImageKey && typeof existingImageKey === 'string') {
        return existingImageKey;
    }
    
    // Return empty string if no image
    return '';
};

/**
 * Helper function to check if a value is a File object
 */
export const isFile = (value: any): value is File => {
    return value instanceof File;
};

/**
 * Helper function to check if a value is a valid string (not empty)
 */
export const isValidString = (value: any): value is string => {
    return typeof value === 'string' && value.trim().length > 0;
};
